import { Alert, Paper, Stack } from '@mui/material';
import { endOfDay, endOfMonth, format, max, min, startOfDay, startOfMonth } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  capabilities,
  cservWorkflowStatus,
  locationArt,
  type RessourcenBlockungen,
  type Terminart,
  terminart as terminartEnum,
  type ThemaFromThemaRouter,
  type Veranstaltung,
  type Zeitraum,
} from '../../../../dtos';
import { durationToTimeUnit } from '../../../../utils/dauer/durationToTimeUnit';
import {
  isAblaufTemplateMissing,
  isOnlineThema,
  isOnlineVeranstaltung,
  isPcCenterThema,
  isTermingruppe,
} from '../../../../utils/index.ts';
import { isPresent } from '../../../../utils/isPresent.ts';
import { getZeitraeumeStartEnd } from '../../../../utils/zeitraum.js';
import { ErrorAlert } from '../../ErrorAlert';
import { PlanningWarning, PlanningWarningDialog } from '../../PlanningWarningDialog.tsx';
import { initFilterParams, isExperteEvent, ResourceCalendar, ResourceCalendarFilters } from '../../ResourceCalendar';
import { isSeminarraumEvent } from '../../ResourceCalendar/helperClasses/CalendarEvent/CalendarEvent.ts';
import { addValidationErrors } from '../../ResourceCalendar/helperClasses/eventUtils.ts';
import { type View, views } from '../../ResourceCalendar/ResourceCalendar.js';
import { StickyWrapper } from '../../StickyWrapper';
import { useCollapseInfo } from '../useCollapseInfo.ts';
import { createPendingEvents, useResourcesAndEvents } from '../useResourcesAndEvents.ts';
import {
  handleExpansionChange,
  onCalendarCellClicked,
  onCalendarEventClicked,
  onCalendarTimeRangeChanged,
  onEventUpdate,
  onEventUpdated,
  onViewChange,
} from '../VivaRessourcenCalendars.logic.ts';
import {
  convertCurrentlySelectedCalendarEventsToBlockungen,
  createCalendarColorsWithSelectionBounds,
  groupCalendarResources,
} from '../VivaRessourcenCalendars.utils.ts';
import { getGruppenraumWarnings, getGruppenraumWarningsBL } from './getGruppenraumWarnings.ts';
import { FullScreen, VeranstaltungPropertiesChanger } from './helperComponents';
import { AblaufCard } from './helperComponents/AblaufCard.js';
import { ActionButtons } from './helperComponents/ActionButtons';
import { ExperteAntworten } from './helperComponents/ExperteAntworten';
import { VorschlaegePruefenHeader } from './helperComponents/VorschlaegePruefenHeader';
import { WarningKeineUebernachtungsmoeglichkeit } from './helperComponents/WarningKeineUebernachtungsmoeglichkeit.js';
import { content } from './VeranstaltungRessourcenCalendar.content.js';
import { type VorschlaegePruefenCalendarConfig } from './VorschlaegePruefenCalendar.types.js';

export type { VorschlaegePruefenCalendarConfig } from './VorschlaegePruefenCalendar.types.js';

export type VeranstaltungRessourcenCalendarProps = {
  readonly isFullscreen: boolean;
  readonly onSave: (
    blockungen: RessourcenBlockungen,
    ablauf: Zeitraum[],
    terminart: NonNullable<Terminart>,
    seminarraumSapId?: number,
  ) => Promise<void>;
  readonly onCancel: () => void | Promise<void>;
  readonly initDate: Date;
  readonly thema: Pick<
    ThemaFromThemaRouter,
    'akaThemaId' | 'planungsinformation' | 'isOnline' | 'planungsformat' | 'module' | 'manuellerAusschreibungsprozess'
  >;
  readonly veranstaltung: Pick<
    Veranstaltung,
    | 'akaModulId'
    | 'akaVeranstaltungId'
    | 'akaDurchgangId'
    | 'ablauf'
    | 'experteBlockungen'
    | 'experteTermine'
    | 'raumBlockungen'
    | 'standortBlockungen'
    | 'teilleistungen'
    | 'capabilities'
    | 'manuellerAusschreibungsprozess'
    | 'onlineTool'
    | 'vivaStatus'
  > | null;
  readonly collapseFocus?: 'experte' | 'raum';
  readonly isVorschlaegePruefenMode?: boolean;
  readonly vorschlaegePruefenConfig?: VorschlaegePruefenCalendarConfig;
};

export const VeranstaltungRessourcenCalendar = ({
  onSave,
  onCancel,
  initDate,
  isFullscreen,
  thema,
  veranstaltung,
  collapseFocus,
  isVorschlaegePruefenMode = false,
  vorschlaegePruefenConfig,
}: VeranstaltungRessourcenCalendarProps) => {
  const canChangeStandort = !veranstaltung || veranstaltung.capabilities.includes(capabilities.CHANGE_STANDORT);
  const canSelectStandort = !veranstaltung || veranstaltung.capabilities.includes(capabilities.SELECT_STANDORT);
  const canEditExperte = !veranstaltung || veranstaltung.capabilities.includes(capabilities.EDIT_EXPERTE);
  const canEditAblauf = !veranstaltung || veranstaltung.capabilities.includes(capabilities.EDIT_ABLAUF);

  const istTerminreihe = Boolean(thema.planungsinformation?.istTerminreihe);
  const modul = thema.module.find((m) => m.akaModulId === veranstaltung?.akaModulId);
  const needsGruppenraum =
    Boolean(thema.planungsinformation?.gruppenraumNotwendig?.length) || //
    Boolean(modul?.gruppenraumInfo);

  const [filterParams, setFilterParams] = useState(
    initFilterParams({
      raumSapIds: (veranstaltung?.raumBlockungen ?? []).map((blockung) => blockung.raumSapId),
      regionSapIds: getInitialRegionSapIds(veranstaltung),
      standortSapIds: getInitialStandortSapIds(veranstaltung),
      raumgroesse: thema.planungsinformation?.raumgroesse ?? null,
      freieTage: thema.planungsinformation?.dauer
        ? Math.ceil(durationToTimeUnit(thema.planungsinformation.dauer, 'days'))
        : null,
      locationHatGruppenraum: needsGruppenraum || null,
      locationArten: isPcCenterThema(thema.planungsinformation)
        ? [locationArt.HOTEL, locationArt.PC_CENTER]
        : [locationArt.HOTEL, locationArt.VERANSTALTUNGSLOCATION],
      sitzordnung: thema.planungsinformation?.sitzordnung ?? null,
      teilnehmerErwartet: thema.planungsinformation?.teilnehmerErwartet ?? null,
    }),
  );
  const [timeRange, setTimeRange] = useState({
    start: startOfMonth(initDate),
    end: endOfMonth(initDate),
  });
  const [view, setView] = useState<View>(views.WEEK);
  const [terminart, setTerminart] = useState<NonNullable<Terminart>>(terminartEnum.STANDARD);
  const [withResources, setWithResources] = useState(true);
  const [ablauf, setAblauf] = useState(veranstaltung?.ablauf ?? []);
  const [planningWarnings, setPlanningWarnings] = useState<PlanningWarning[]>([]);
  const appliedVorschlagKey = useRef<string | undefined>(undefined);

  const vorschlagZeitraeume = vorschlaegePruefenConfig?.selectedVorschlag?.leistungszeitraeume;
  const ressourcenTimeRange = useMemo(() => {
    if (!vorschlagZeitraeume?.length) {
      return timeRange;
    }

    const vorschlagRange = getZeitraeumeStartEnd(vorschlagZeitraeume);

    return {
      start: min([timeRange.start, startOfDay(vorschlagRange.start)]),
      end: max([timeRange.end, endOfDay(vorschlagRange.end)]),
    };
  }, [timeRange, vorschlagZeitraeume]);

  if (!thema.planungsinformation) {
    throw new Error('thema.planungsinformation is null');
  }

  const {
    seminarraumResources,
    gruppenraumResources,
    standortResources,
    experteResources,
    fixedEvents,
    pendingEvents,
    setPendingEvents,
    clearState,
  } = useResourcesAndEvents({
    akaThemaId: thema.akaThemaId,
    planungsinformation: thema.planungsinformation,
    skipTrainerGueltigkeitsCheck: isTermingruppe(thema.planungsformat),
    akaVeranstaltungId: veranstaltung?.akaVeranstaltungId ?? null,
    filterParams,
    timeRange: ressourcenTimeRange,
    view,
  });

  useEffect(() => {
    const selectedVorschlag = vorschlaegePruefenConfig?.selectedVorschlag;
    const selectedVorschlagKey = vorschlaegePruefenConfig?.selectedVorschlagKey;

    if (
      selectedVorschlag === undefined ||
      selectedVorschlagKey === undefined ||
      appliedVorschlagKey.current === selectedVorschlagKey
    ) {
      return;
    }

    const resources = [...seminarraumResources, ...gruppenraumResources, ...standortResources, ...experteResources];
    if (!experteResources.some((resource) => resource.experte.experteSapId === selectedVorschlag.experteSapId)) {
      return;
    }

    const seminarraumSapId = veranstaltung?.raumBlockungen.find((blockung) => !blockung.raum.isGruppenraum)?.raumSapId;
    const gruppenraumSapIds = veranstaltung?.raumBlockungen
      .filter((blockung) => blockung.raum.isGruppenraum)
      .map((blockung) => blockung.raumSapId);
    const suggestedEvents = {
      seminarraumSapId,
      tage: selectedVorschlag.leistungszeitraeume.map((zeitraum) => ({
        zeitraum,
        expertenSapIds: [selectedVorschlag.experteSapId],
        gruppenraumSapIds,
      })),
    };
    const proposalEvents = createPendingEvents(
      resources,
      suggestedEvents,
      veranstaltung?.akaVeranstaltungId ?? null,
      view,
    );

    for (const event of proposalEvents) {
      event.resize = false;
      event.dragInTime = false;
    }
    addValidationErrors(
      proposalEvents,
      fixedEvents,
      istTerminreihe,
      isPcCenterThema(thema.planungsinformation),
      seminarraumResources,
      experteResources,
    );

    const applySelectionTimeout = setTimeout(() => {
      setAblauf(selectedVorschlag.leistungszeitraeume);
      setPendingEvents(proposalEvents);
      appliedVorschlagKey.current = selectedVorschlagKey;
    }, 0);

    return () => clearTimeout(applySelectionTimeout);
  }, [
    experteResources,
    fixedEvents,
    gruppenraumResources,
    istTerminreihe,
    seminarraumResources,
    standortResources,
    thema.planungsinformation,
    veranstaltung,
    view,
    vorschlaegePruefenConfig?.selectedVorschlag,
    vorschlaegePruefenConfig?.selectedVorschlagKey,
    setPendingEvents,
  ]);

  const isPruefenMode = isVorschlaegePruefenMode;
  useEffect(() => {
    if (!isPruefenMode) {
      return;
    }

    setPendingEvents((previousEvents) => {
      if (previousEvents.length === 0) {
        return previousEvents;
      }

      const revalidatedEvents = [...previousEvents];
      addValidationErrors(
        revalidatedEvents,
        fixedEvents,
        istTerminreihe,
        isPcCenterThema(thema.planungsinformation),
        seminarraumResources,
        experteResources,
      );

      return revalidatedEvents;
    });
  }, [
    experteResources,
    fixedEvents,
    isPruefenMode,
    istTerminreihe,
    seminarraumResources,
    setPendingEvents,
    thema.planungsinformation,
  ]);

  const sichtbareFixedEvents = vorschlaegePruefenConfig?.selectedVorschlag
    ? fixedEvents.filter(
        (event) =>
          !isExperteEvent(event) || !vorschlaegePruefenConfig.weitereExperteSapIds.includes(event.ressourceSapId),
      )
    : fixedEvents;
  const fixedAndPendingEvents = [...sichtbareFixedEvents, ...pendingEvents];
  const isOnline =
    isTermingruppe(thema.planungsformat) && veranstaltung
      ? isOnlineVeranstaltung(veranstaltung) // for BLs we can have a mix of online and präsenz VAs
      : isOnlineThema(thema); // for new VAs we always go from the thema (BLs have their specific way of handling this afterwards)
  const manuellerAusschreibungsprozess =
    veranstaltung?.manuellerAusschreibungsprozess ?? thema.manuellerAusschreibungsprozess;

  const [collapseInfo, setCollapseInfo] = useCollapseInfo(pendingEvents, collapseFocus);

  const invalidDates = useMemo(() => {
    if (canEditAblauf || ablauf.length === 0) return undefined;
    return [
      {
        allDay: true,
        recurring: { repeat: 'daily' as const },
        recurringException: ablauf.map((z) => startOfDay(z.start)),
      },
    ];
  }, [canEditAblauf, ablauf]);

  if (!thema.planungsinformation.zeitlicherAblauf) {
    return <ErrorAlert text="Thema ohne Planungsinformation" />;
  }

  const isSelectionOutsideView = (): boolean => {
    if (pendingEvents.length === 0) {
      return false;
    }

    const { start, end } = getZeitraeumeStartEnd(pendingEvents);

    return start < timeRange.start || end > timeRange.end;
  };

  if (!isTermingruppe(thema.planungsformat) && isAblaufTemplateMissing(thema)) {
    return <Alert severity="warning">{content.fehler.missingAblaufTemplate}</Alert>;
  }

  // UI event handlers
  const handleWithResourcesChange = (value: boolean): void => {
    setPendingEvents([]);
    setAblauf([]);
    setWithResources(value);
  };

  const handleCancel = async (): Promise<void> => {
    clearState();
    await onCancel();
  };

  const handleSave = async (): Promise<void> => {
    const warnings = needsGruppenraum
      ? isTermingruppe(thema.planungsformat)
        ? getGruppenraumWarningsBL(pendingEvents)
        : getGruppenraumWarnings(pendingEvents, thema.planungsinformation?.gruppenraumNotwendig ?? [], ablauf)
      : [];

    setPlanningWarnings(warnings);

    if (warnings.length === 0) await performSave();
  };

  // Warning dialog handlers
  const handleWarningCancel = (): void => {
    setPlanningWarnings([]);
  };

  const handleWarningApprove = async (): Promise<void> => {
    setPlanningWarnings([]);
    await performSave();
  };

  // Core save logic
  const performSave = async (): Promise<void> => {
    const ressourcenBlockungen = convertCurrentlySelectedCalendarEventsToBlockungen(pendingEvents, ablauf);
    const seminarraumSapId = pendingEvents.find(isSeminarraumEvent)?.ressourceSapId;

    await onSave(ressourcenBlockungen, ablauf, terminart, seminarraumSapId);
    clearState();
  };

  const zeitlicherAblauf = veranstaltung
    ? veranstaltung.ablauf.map((tag, index) => ({
        tagNr: index,
        startZeit: format(tag.start, 'HH:mm'),
        endZeit: format(tag.end, 'HH:mm'),
      }))
    : thema.planungsinformation.zeitlicherAblauf;

  // the StickyWrapper is a hacky solution to break the actions buttons free from their component container and make them sticky to the header
  return (
    <>
      <FullScreen isFullscreen={isFullscreen}>
        {isVorschlaegePruefenMode && vorschlaegePruefenConfig ? (
          <VorschlaegePruefenHeader
            modulLabel={vorschlaegePruefenConfig.modulLabel}
            canUebernehmen={
              vorschlaegePruefenConfig.selectedVorschlag !== undefined &&
              !vorschlaegePruefenConfig.isUebernehmenPending &&
              !vorschlaegePruefenConfig.isAuswahlAufhebenPending &&
              !vorschlaegePruefenConfig.isVerwerfenPending &&
              !vorschlaegePruefenConfig.isVerwerfenDialogOpen
            }
            isUebernehmenPending={vorschlaegePruefenConfig.isUebernehmenPending}
            isVerwerfenDisabled={
              (vorschlaegePruefenConfig.isUebernehmenPending ?? false) ||
              (vorschlaegePruefenConfig.isAuswahlAufhebenPending ?? false) ||
              (vorschlaegePruefenConfig.isVerwerfenPending ?? false) ||
              (vorschlaegePruefenConfig.isVerwerfenDialogOpen ?? false)
            }
            isCloseDisabled={
              (vorschlaegePruefenConfig.isUebernehmenPending ?? false) ||
              (vorschlaegePruefenConfig.isAuswahlAufhebenPending ?? false) ||
              (vorschlaegePruefenConfig.isVerwerfenPending ?? false)
            }
            canAuswahlAufheben={vorschlaegePruefenConfig.canAuswahlAufheben}
            isAuswahlAufhebenPending={vorschlaegePruefenConfig.isAuswahlAufhebenPending}
            onAuswahlAufheben={vorschlaegePruefenConfig.onAuswahlAufheben}
            onUebernehmen={handleSave}
            onVerwerfenAndUmplanen={vorschlaegePruefenConfig.onDiscardAllVorschlaege}
            onClose={handleCancel}
          />
        ) : (
          <StickyWrapper disableSticky={isFullscreen} sx={{ top: '24px', mt: '-82px', pr: '16px' }}>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'flex-end',
                my: 3,
                mr: isFullscreen ? 3 : 0,
              }}
            >
              <ActionButtons
                isOnlineThema={isOnline}
                ablauf={ablauf}
                withResources={withResources}
                selectedCalendarEvents={pendingEvents}
                onCancel={handleCancel}
                onSave={handleSave}
                capabilities={veranstaltung?.capabilities}
              />
            </Stack>
          </StickyWrapper>
        )}
        <Stack direction="column">
          {!isFullscreen && (
            <VeranstaltungPropertiesChanger
              terminart={terminart}
              withRessourcen={withResources}
              onTerminartChange={setTerminart}
              onWithRessourcenChange={handleWithResourcesChange}
              manuellerAusschreibungsprozess={manuellerAusschreibungsprozess}
            />
          )}

          {!withResources && (
            <AblaufCard
              ablauf={ablauf}
              onAblaufChange={setAblauf}
              zeitlicherAblauf={thema.planungsinformation.zeitlicherAblauf}
            />
          )}

          {withResources && (
            <Paper sx={{ overflow: 'hidden', pb: 0, borderWidth: isFullscreen ? 0 : 1 }}>
              <Stack>
                <Stack direction="column" gap={2} m={3} mb={0} mt={isFullscreen ? 0 : 3}>
                  <ExperteAntworten
                    veranstaltung={veranstaltung}
                    isVorschlaegePruefenMode={isVorschlaegePruefenMode}
                    vorschlagSelection={vorschlaegePruefenConfig}
                  />
                  <WarningKeineUebernachtungsmoeglichkeit
                    thema={thema}
                    pendingEvents={pendingEvents}
                    seminarraumResources={seminarraumResources}
                  />
                  {thema.planungsinformation.cservWorkflowStatus === cservWorkflowStatus.PLANUNG_INAKTIV && (
                    <Alert severity="warning">{content.warnungen.planungInaktiv}</Alert>
                  )}
                  {isSelectionOutsideView() && (
                    <Alert severity="warning">{content.warnungen.selectionOutsideView}</Alert>
                  )}
                </Stack>

                {!isOnline && (
                  <ResourceCalendarFilters
                    filterParameters={filterParams}
                    startYear={timeRange.start.getFullYear()}
                    lockedFilters={
                      (!canChangeStandort && canSelectStandort) || isVorschlaegePruefenMode
                        ? { regionSapIds: true, standortSapIds: true }
                        : undefined
                    }
                    onFilterChange={(filter) => {
                      setFilterParams(filter);
                    }}
                  />
                )}
                {/*
                  This height is important, the calendar needs to be contained in a container with a height, see
                  https://mobiscroll.com/docs/react/eventcalendar/api#opt-view.

                  Without this height, https://jira.haufe.io/browse/AKAVM-2370 will occur again.
                */}
                <div className="h-[calc(100vh-100px)]">
                  <ResourceCalendar
                    key={vorschlaegePruefenConfig?.selectedVorschlagKey ?? 'original-selection'}
                    initView={view}
                    resources={groupCalendarResources({
                      seminarraumResources,
                      gruppenraumResources,
                      standortResources,
                      experteResources,
                      collapseInfo,
                      planungsinformation: thema.planungsinformation,
                      isOnlineVeranstaltung: isOnline,
                      isModultermin: isTermingruppe(thema.planungsformat),
                      events: fixedAndPendingEvents,
                      includeGruppenraeume: needsGruppenraum,
                      includeStandorte: canChangeStandort || canSelectStandort || isVorschlaegePruefenMode,
                      includeExperten: canEditExperte || isVorschlaegePruefenMode,
                    })}
                    events={fixedAndPendingEvents}
                    colors={createCalendarColorsWithSelectionBounds(
                      seminarraumResources,
                      gruppenraumResources,
                      standortResources,
                      experteResources,
                      fixedAndPendingEvents,
                      ablauf,
                      timeRange,
                    )}
                    timeRange={timeRange}
                    initialDate={
                      vorschlaegePruefenConfig?.selectedVorschlag?.leistungszeitraeume.at(0)?.start ??
                      (veranstaltung ? initDate : timeRange.start)
                    }
                    invalid={invalidDates}
                    timeRangeChangeHandler={(newTimeRange) => onCalendarTimeRangeChanged(newTimeRange, setTimeRange)}
                    toggleExpansion={(resourceType, expanded) =>
                      handleExpansionChange(resourceType, expanded, setCollapseInfo)
                    }
                    onViewChange={(_oldView, newView) =>
                      onViewChange(_oldView, newView, setPendingEvents, setView, isVorschlaegePruefenMode)
                    }
                    cellClickHandler={(cellClickEvent) => {
                      onCalendarCellClicked({
                        args: cellClickEvent,
                        ablauf,
                        pendingEvents,
                        isPcCenterThema: isPcCenterThema(thema.planungsinformation),
                        zeitlicherAblauf,
                        istTerminreihe,
                        fixedEvents,
                        seminarraumResources,
                        gruppenraumResources,
                        standortResources,
                        experteResources,
                        view,
                        setAblauf,
                        setPendingEvents,
                        canEditAblauf: isVorschlaegePruefenMode ? false : canEditAblauf,
                        canEditExperte: !isVorschlaegePruefenMode && canEditExperte,
                      });
                    }}
                    eventClickHandler={
                      isVorschlaegePruefenMode
                        ? undefined
                        : (eventClickEvent) =>
                            onCalendarEventClicked(
                              eventClickEvent,
                              setPendingEvents,
                              setAblauf,
                              zeitlicherAblauf,
                              veranstaltung?.ablauf,
                            )
                    }
                    onEventUpdate={(updateEvent) =>
                      onEventUpdate(updateEvent, ablauf, istTerminreihe, view, pendingEvents, isVorschlaegePruefenMode)
                    }
                    onEventUpdated={(updatedEvent) =>
                      onEventUpdated({
                        updateEvent: updatedEvent,
                        pendingEvents,
                        ablauf,
                        setAblauf,
                        zeitlicherAblauf,
                        isPcCenterThema: isPcCenterThema(thema.planungsinformation),
                        istTerminreihe,
                        fixedEvents,
                        seminarraumResources,
                        experteResources,
                        setPendingEvents,
                      })
                    }
                  />
                </div>
              </Stack>
            </Paper>
          )}
        </Stack>
      </FullScreen>
      <PlanningWarningDialog
        open={planningWarnings.length > 0}
        warnings={planningWarnings}
        onCancel={handleWarningCancel}
        onApprove={handleWarningApprove}
      />
    </>
  );
};

function getInitialStandortSapIds(veranstaltung: VeranstaltungRessourcenCalendarProps['veranstaltung']): number[] {
  if (!veranstaltung) return [];

  if (veranstaltung.raumBlockungen.length > 0) {
    return veranstaltung.raumBlockungen
      .map((raumBlockung) => raumBlockung.raum.location.standort?.standortSapId)
      .filter(isPresent);
  }

  if (veranstaltung.standortBlockungen.length > 0) {
    return veranstaltung.standortBlockungen
      .map((standortBlockung) => standortBlockung.standort.standortSapId)
      .filter(isPresent);
  }

  return [];
}

function getInitialRegionSapIds(veranstaltung: VeranstaltungRessourcenCalendarProps['veranstaltung']): number[] {
  if (!veranstaltung) return [];

  if (veranstaltung.raumBlockungen.length > 0) {
    return veranstaltung.raumBlockungen
      .map((raumBlockung) => raumBlockung.raum.location.region?.regionSapId)
      .filter(isPresent);
  }

  if (veranstaltung.standortBlockungen.length > 0) {
    return veranstaltung.standortBlockungen
      .map((standortBlockung) => standortBlockung.standort.region?.regionSapId)
      .filter(isPresent);
  }

  return [];
}
